import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import TuneIcon from '@mui/icons-material/Tune';
import { usePlayground } from '../../shared/context/PlaygroundContext';
import { parseSlashQuery, filterPromptsByQuery } from './promptComposer';
import { promptCategories } from '../prompts/promptsData';

const Wrap = styled.div`
  position: relative;
`;





const Dropdown = styled.div`
  font-family: var(--font-secondary);
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin-bottom: 8px;
  max-height: 280px;
  overflow-y: auto;
  background: ${({ theme }) => theme.modal_background || theme.sidebar_background_solid || theme.sidebar_background};
  border: 1px solid ${({ theme }) => theme.modal_border || theme.sidebar_border};
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.22);
  z-index: 40;
  padding: 4px;
`;

const Item = styled.button`
  width: 100%;
  text-align: left;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  &:hover { background: ${({ theme }) => (theme.font_color || '#000') + '10'}; }
`;

const ItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const ItemTitle = styled.span`
  font-weight: 600;
  font-size: 0.86rem;
`;

const ItemDesc = styled.span`
  font-size: 0.75rem;
  opacity: 0.6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Hint = styled.div`
  padding: 8px 12px;
  font-size: 0.75rem;
  opacity: 0.55;
`;

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.modal_border || theme.sidebar_border};
  margin-bottom: 4px;
`;

const MenuHeaderLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.55;
  color: ${({ theme }) => theme.font_color};
`;

const OpenLibraryBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.prompt_accent_color};
  padding: 3px 8px;
  border-radius: 6px;
  &:hover { background: ${({ theme }) => (theme.font_color || '#000') + '10'}; }
`;

/**
 * Composer overlay for the prompt library (features B & C):
 *  - a "/" at the start of the composer opens a filterable prompt menu;
 *  - selecting a prompt opens the existing filler and, on submit, attaches a
 *    named chip (instead of dumping the template text into the textarea).
 */
const ComposerPromptUI: React.FC = () => {
  const { currentMessage, setCurrentMessage, openPromptFiller, setPromptBankOpen } = usePlayground();

  const allPrompts = useMemo(() => {
    // Flatten with the category name attached (so "/saude" also finds the
    // Saude & Bem-Estar prompts) and dedup by id (some prompts repeat
    // across categories and would show twice in the menu).
    try {
      const seen = new Set<string>();
      const list: any[] = [];
      for (const c of promptCategories) {
        for (const p of c.prompts) {
          if (seen.has(p.id)) continue;
          seen.add(p.id);
          list.push({ ...p, categoryName: c.name });
        }
      }
      return list;
    } catch { return []; }
  }, []);

  const slash = useMemo(() => parseSlashQuery(currentMessage), [currentMessage]);
  const matches = useMemo(
    () => (slash.active ? filterPromptsByQuery(allPrompts as any, slash.query) : []),
    [slash, allPrompts]
  );
  const visible = matches; // dropdown scrolls (max-height + overflow-y)

  // Clicking outside the dropdown dismisses it while in "/" mode; typing
  // any new character (message change) brings it back.
  const [dismissed, setDismissed] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { setDismissed(false); }, [currentMessage]);

  useEffect(() => {
    if (!slash.active) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDismissed(true);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDismissed(true);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [slash.active]);

  const menuOpen = slash.active && !dismissed;

  const choose = (p: any) => {
    setCurrentMessage('');
    openPromptFiller(p);
  };

  return (
    <Wrap>
      {menuOpen && (
        <Dropdown ref={dropdownRef} data-testid="slash-menu">
          <MenuHeader>
            <MenuHeaderLabel>Biblioteca de prompts</MenuHeaderLabel>
            <OpenLibraryBtn
              // onMouseDown para disparar antes do blur da textarea
              onMouseDown={(e) => { e.preventDefault(); setCurrentMessage(''); setPromptBankOpen(true); }}
            >
              Abrir biblioteca
            </OpenLibraryBtn>
          </MenuHeader>
          {visible.length === 0 ? (
            <Hint>Nenhum prompt encontrado — crie o seu na biblioteca</Hint>
          ) : (
            visible.map((p: any) => (
              <Item
                key={p.id}
                data-testid="slash-item"
                // onMouseDown (not onClick) so selection fires before the textarea blurs.
                onMouseDown={(e) => { e.preventDefault(); choose(p); }}
              >
                <TuneIcon style={{ fontSize: 18, opacity: 0.6, marginTop: 1 }} />
                <ItemText>
                  <ItemTitle>{p.title}</ItemTitle>
                  {p.description && <ItemDesc>{p.description}</ItemDesc>}
                </ItemText>
              </Item>
            ))
          )}
        </Dropdown>
      )}
    </Wrap>
  );
};

export default ComposerPromptUI;
